import { makeEventNamespace } from "~/core/dom/events";
import { initSection } from "~/core/shopify/init-section";
import "~/styles/sections/story-timeline.css";

initSection(".js-story-timeline-section", (section) => {
    const namespace = makeEventNamespace();
    const root = section.querySelector<HTMLElement>("[data-story-timeline]");
    const track = section.querySelector<HTMLElement>("[data-story-track]");
    const milestones = Array.from(
        section.querySelectorAll<HTMLElement>("[data-story-milestone]"),
    );
    const buttons = Array.from(
        section.querySelectorAll<HTMLButtonElement>("[data-story-nav-button]"),
    );
    const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
    );
    let observer: IntersectionObserver | null = null;

    const years = section.querySelector<HTMLElement>(".story-timeline__years");
    const compact = window.matchMedia("(max-width: 1023px)");

    let activeIndex = -1;
    const setActive = (index: number): void => {
        if (index === activeIndex) return;
        activeIndex = index;
        buttons.forEach((button, buttonIndex) => {
            const active = buttonIndex === index;
            button.classList.toggle("is-active", active);
            button.setAttribute("aria-current", String(active));
        });

        const activeButton = buttons[index];
        if (years === null || activeButton === undefined || !compact.matches) {
            if (years !== null) {
                years.style.transform = "";
            }
            return;
        }

        const shift =
            years.offsetHeight / 2 -
            (activeButton.offsetTop + activeButton.offsetHeight / 2);
        years.style.transform = `translateY(calc(-50% + ${shift}px))`;
    };

    buttons.forEach((button) => {
        namespace.addDirectEventListener(button, "click", () => {
            const index = Number(button.dataset.storyNavButton);
            const milestone = milestones[index];
            if (milestone === undefined) return;
            milestone.scrollIntoView({
                behavior: reducedMotion.matches ? "auto" : "smooth",
                block: "nearest",
                inline: "start",
            });
            setActive(index);
        });
    });

    // Desktop: a vertical wheel steps through the horizontal track one year at a time while the
    // timeline fills the screen, then hands the wheel back to the page at the first or last year.
    // Trackpad sideways swipes keep scrolling the track natively.
    let wheelDelta = 0;
    let wheelLockedUntil = 0;
    const currentIndex = (): number => {
        if (track === null) return 0;
        const trackLeft = track.getBoundingClientRect().left;
        let closest = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        milestones.forEach((milestone, index) => {
            const distance = Math.abs(
                milestone.getBoundingClientRect().left - trackLeft,
            );
            if (distance < closestDistance) {
                closest = index;
                closestDistance = distance;
            }
        });
        return closest;
    };
    const fillsViewport = (element: HTMLElement): boolean => {
        const rect = element.getBoundingClientRect();
        const visible =
            Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        return visible >= window.innerHeight * 0.85;
    };

    if (track !== null && milestones.length > 1) {
        namespace.addDirectEventListener(
            track,
            "wheel",
            (_track, evt) => {
                if (!(evt instanceof WheelEvent)) return;
                const event = evt;
                if (compact.matches || event.ctrlKey) return;
                if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
                if (!fillsViewport(track)) return;

                const direction = Math.sign(event.deltaY);
                const target = milestones[currentIndex() + direction];
                if (target === undefined) {
                    wheelDelta = 0;
                    return;
                }

                event.preventDefault();
                if (performance.now() < wheelLockedUntil) return;
                if (Math.sign(wheelDelta) !== direction) wheelDelta = 0;
                wheelDelta += event.deltaY;
                if (Math.abs(wheelDelta) < 30) return;

                wheelDelta = 0;
                wheelLockedUntil = performance.now() + 700;
                track.scrollTo({
                    left:
                        track.scrollLeft +
                        target.getBoundingClientRect().left -
                        track.getBoundingClientRect().left,
                    behavior: reducedMotion.matches ? "auto" : "smooth",
                });
            },
            { passive: false },
        );
    }

    // Mobile stacks the milestones vertically. The active year is the milestone that has crossed
    // the middle of the screen, read on scroll. The observer only reported entries that crossed a
    // threshold, so the year you were leaving could re-activate for a moment and flash.
    let scrollRequest = 0;
    const syncFromViewport = (): void => {
        scrollRequest = 0;
        if (!compact.matches) return;
        const middle = window.innerHeight / 2;
        let index = 0;
        milestones.forEach((milestone, milestoneIndex) => {
            if (milestone.getBoundingClientRect().top <= middle) {
                index = milestoneIndex;
            }
        });
        setActive(index);
    };
    const queueSync = (): void => {
        if (scrollRequest === 0) {
            scrollRequest = requestAnimationFrame(syncFromViewport);
        }
    };
    namespace.addDocumentEventListener("scroll", queueSync, { passive: true });
    window.addEventListener("resize", queueSync);

    if (track !== null && milestones.length > 0) {
        root?.setAttribute("data-enhanced", "true");
        observer = new IntersectionObserver(
            (entries) => {
                if (compact.matches) return;
                const active = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort(
                        (left, right) =>
                            right.intersectionRatio - left.intersectionRatio,
                    )[0];
                if (!(active?.target instanceof HTMLElement)) return;
                const index = milestones.indexOf(active.target);
                if (index >= 0) setActive(index);
            },
            {
                root: track,
                threshold: [0.45, 0.6],
            },
        );
        milestones.forEach((milestone) => observer?.observe(milestone));
        syncFromViewport();
    }

    return {
        unload: () => {
            namespace.destroy();
            observer?.disconnect();
            window.removeEventListener("resize", queueSync);
            cancelAnimationFrame(scrollRequest);
        },
        selectBlock: (block) => {
            const milestone = block.closest<HTMLElement>(
                "[data-story-milestone]",
            );
            if (milestone === null) return;
            const index = milestones.indexOf(milestone);
            milestone.scrollIntoView({
                behavior: "auto",
                block: "nearest",
                inline: "start",
            });
            if (index >= 0) setActive(index);
        },
    };
});
